import React, { useEffect, useState, useRef } from 'react'
import axios from 'axios'
import ClassicCenteredTrainStatusComponent from './ClassicCenteredTrainStatusComponent/ClassicCenteredTrainStatusComponent.js'
import ClassicLeftBoundTrainStatusComponent from './ClassicLeftBoundTrainStatusComponent/ClassicLeftBoundTrainStatusComponent.js'
import ClassicBusRow from './ClassicBusRow/ClassicBusRow.js'
import { themes } from '../enums/enums.js'
import './view.css'

export default function View({ listOfStationsToDisplay, selectedTheme }) {
  const [displayedData, setDisplayedData] = useState(null)
  const [busBannerTick, setBusBannerTick] = useState(0)
  const [userIdUi, setUserIdUi] = useState('')
  const counterRef = useRef(0)
  const firstLoad = useRef(true)
  const prefetchedRef = useRef(null) // store prefetched data here (no re-render)

  // Fetch data helper
  async function fetchBusAndTrainData() {
    try {
      const userId = localStorage.getItem('UserId')
      if (!userId) {
        console.error('UserId not found in localStorage')
        setDisplayedData([])
        return
      }

      const response = await axios.get(
        process.env.REACT_APP_ENVIRONMENT + '/api/trains',
        { params: { userId } }
      )

      const resultList = response.data?.bussesAndTrainsList || []
      prefetchedRef.current = resultList // store silently (no rerender)
      console.log('Prefetched data stored (no rerender)')
    } catch (error) {
      console.error('Failed to fetch train information:', error)
      prefetchedRef.current = []
    }
  }

  // Initial load
  useEffect(() => {
    async function init() {
      const userId = localStorage.getItem('UserId')
      if (!userId) return setDisplayedData([])

      try {
        const response = await axios.get(
          process.env.REACT_APP_ENVIRONMENT + '/api/trains',
          { params: { userId } }
        )
        const resultList = response.data?.bussesAndTrainsList || []
        setDisplayedData(resultList)
        prefetchedRef.current = resultList // keep synced
        setUserIdUi('')
      } catch (error) {
        console.error('Initial data fetch failed:', error)
        setDisplayedData([])
      }
    }
    init()
  }, [])

  // Interval manager
  useEffect(() => {
    const tick = setInterval(() => {
      counterRef.current++

      // Every 6th tick (~24s): prefetch new data (NO rerender)
      if (counterRef.current >= 6) {
        fetchBusAndTrainData()
        counterRef.current = 0
        console.log('Request made (no rerender)')
      }
      // Every 4th tick (~16s): swap prefetched → displayed (triggers rerender)
      else if (counterRef.current === 4) {
        if (prefetchedRef.current) {
          setDisplayedData(prefetchedRef.current)
          console.log('Swap → new data rendered')
        } else {
          console.warn('Skipped swap — no prefetched data yet')
        }
      }

      // Every tick (except request tick): smooth banner animation
      //if (counterRef.current !== 6) {
        setBusBannerTick(prev => prev + 1)
      //}
    }, 4000)

    return () => clearInterval(tick)
  }, [])

  // RENDERERS
  function renderBusBanner(busRow, id) {
    return (
      <ClassicBusRow
        key={id}
        busses={busRow}
        busBannerTick={busBannerTick}
        bannerId={id}
      />
    )
  }

  function renderTrainBanner(singleTrainBanner, id) {
    const trainData = buildTrainData(singleTrainBanner)
    return selectedTheme === themes.ClassicCentered ? (
      <ClassicCenteredTrainStatusComponent trainData={trainData} bannerId={id} key={id} />
    ) : (
      <ClassicLeftBoundTrainStatusComponent trainData={trainData} bannerId={id} key={id} />
    )
  }

  function buildTrainData(train) {
    const t = train || {}
    const dir1 = t.trainDirectionOneInformation?.trainArrivalDetails || []
    const dir5 = t.trainDirectionFiveInformation?.trainArrivalDetails || []

    return {
      stationId: t.stationId || '',
      stationName: t.stationName || '',
      trainColorDisplay: t.trainColorDisplay || '',
      trainColorHexValue: t.trainColorHexValue || '',
      directionOneDestinationName: t.trainDirectionOneInformation?.destinationName || '',
      directionFiveDestinationName: t.trainDirectionFiveInformation?.destinationName || '',
      directionOneTimeOne: dir1[0]?.isApproaching === '1' ? 'Due' : dir1[0]?.arrivalTime || '-',
      directionOneTimeTwo: dir1[1]?.arrivalTime || '-',
      directionOneTimeThree: dir1[2]?.arrivalTime || '-',
      directionFiveTimeOne: dir5[0]?.isApproaching === '1' ? 'Due' : dir5[0]?.arrivalTime || '-',
      directionFiveTimeTwo: dir5[1]?.arrivalTime || '-',
      directionFiveTimeThree: dir5[2]?.arrivalTime || '-',
    }
  }

  return (
    <div className="view-container">
      {displayedData
        ? displayedData.map((item, idx) =>
            item.isBus
              ? renderBusBanner(item, idx + 1)
              : renderTrainBanner(item.train, idx + 1)
          )
        : <div>Loading data...</div>}
    </div>
  )
}
